import {
  datamuseQuery,
  definitionFor,
  type PartOfSpeech,
  splitEcho,
  type WordResult,
} from "@/lib/tool-kit/vocabulary/datamuse";

/**
 * The Antonym Finder's questions.
 *
 * Antonyms come from `rel_ant`, which is WordNet's antonym relation — a short,
 * hand-curated list, and an honest one: most nouns have no antonym at all. When it is
 * empty the widget says so and asks two further questions instead of padding the list:
 * whether a negative prefix makes a real word (`biased` → `unbiased`), and which words
 * are merely related in meaning, labelled as such.
 */

const MAX_ANTONYMS = 50;
const MAX_RELATED = 24;

export function antonymQuery(word: string): string {
  return datamuseQuery({
    rel_ant: word,
    qe: "rel_ant",
    md: "dpf",
    max: MAX_ANTONYMS,
  });
}

export function relatedQuery(word: string): string {
  return datamuseQuery({ ml: word, md: "dp", max: MAX_RELATED });
}

export interface AntonymEntry {
  word: string;
  pos: PartOfSpeech;
  definition: string | null;
}

function toEntry(result: WordResult): AntonymEntry {
  const pos = result.pos[0] ?? "u";
  return {
    word: result.word,
    pos,
    definition: definitionFor(result, pos)?.text ?? null,
  };
}

/** The searched word (from the echo) and its antonyms, minus the word itself. */
export function antonymEntries(
  word: string,
  results: readonly WordResult[]
): { echo: WordResult | null; entries: AntonymEntry[] } {
  const { echo, results: rest } = splitEcho(results);
  const searched = word.toLowerCase();
  const seen = new Set<string>();
  const entries: AntonymEntry[] = [];
  for (const result of rest) {
    const key = result.word.toLowerCase();
    if (key === searched || seen.has(key)) {
      continue;
    }
    seen.add(key);
    entries.push(toEntry(result));
  }
  return { echo, entries };
}

/** Related words, for the case with no antonyms; never presented as opposites. */
export function relatedEntries(
  word: string,
  results: readonly WordResult[]
): AntonymEntry[] {
  return antonymEntries(word, results).entries;
}

/* ----------------------------------------------------------------- prefixes */

const RE_PLAIN_WORD = /^[a-z]{3,}$/;

/**
 * `in-` assimilates to the sound after it: `im-` before b, m and p (`impossible`,
 * `immature`, `imbalance`), `il-` before l (`illegal`), `ir-` before r (`irregular`).
 */
export function negativeInPrefix(word: string): "in" | "im" | "il" | "ir" {
  const first = word.charAt(0);
  if (first === "b" || first === "m" || first === "p") {
    return "im";
  }
  if (first === "l") {
    return "il";
  }
  if (first === "r") {
    return "ir";
  }
  return "in";
}

/**
 * The prefixed forms worth checking: un-, the right form of in-, dis-, and non- (closed
 * and hyphenated). Only for a plain lower-case word of three or more letters.
 */
export function prefixCandidates(word: string): string[] {
  const base = word.toLowerCase();
  if (!RE_PLAIN_WORD.test(base)) {
    return [];
  }
  return [
    `un${base}`,
    `${negativeInPrefix(base)}${base}`,
    `dis${base}`,
    `non${base}`,
    `non-${base}`,
  ];
}

/** Ask about one candidate through the query echo: its frequency and definitions. */
export function prefixQuery(candidate: string): string {
  return datamuseQuery({ sp: candidate, qe: "sp", md: "dpf", max: 1 });
}

/**
 * The candidates that are real words: the echo has a definition. Datamuse echoes any
 * string, so an invented form comes back too — with no definition and a frequency of
 * zero, which is how it is told apart. Commonest first.
 */
export function prefixedForms(
  candidates: readonly string[],
  answers: readonly (readonly WordResult[])[]
): AntonymEntry[] {
  const found: { entry: AntonymEntry; frequency: number }[] = [];
  for (const [index, candidate] of candidates.entries()) {
    const echo = splitEcho(answers[index] ?? []).echo;
    if (echo && echo.definitions.length > 0) {
      found.push({
        entry: { ...toEntry(echo), word: candidate },
        frequency: echo.frequency ?? 0,
      });
    }
  }
  return found
    .sort((a, b) => b.frequency - a.frequency)
    .map((item) => item.entry);
}
